import { State, Action, StateContext, Store } from '@ngxs/store';
import {
  SetAvailableForChangeOfferingsAction,
  ApiGetOfferingsAction,
  ApiGetOfferingsSuccessAction,
  ApiGetAdditionalServiceOfferingsAction,
  ApiGetAdditionalServiceOfferingsSuccessAction,
  ApiGetAdditionalServiceOfferingsErrorAction,
  OfferingResetAction,
  ApiGetOfferingsErrorAction,
} from './offering.actions';
import { catchError, filter, tap } from 'rxjs/operators';
import { AdditionalServicesOffering } from '@shared/models/additional-services-offering.model';
import { Injectable, inject } from '@angular/core';
import { VoiceSubscriptionOffering } from '@sales/modules/subscription/models/offerings-response.model';
import { OfferingService } from '@sales/modules/subscription/services/offering.service';
import { NotificationService } from '@core/services/notification.service';
import {
  AdditionalServiceAddAction,
  CombineExistingSubscriptionServicesWithOfferingServices,
} from '../customer-product/customer-product.actions';
import {
  AdditionalServiceSet,
  AdditionalServiceVoice,
} from '../../modules/subscription/models/additional-service-voice.model';

enum Messages {
  ErrorOnFetchingAdditionalServices = 'Feil ved henting av tilleggstjenester',
  ErrorOnFetchingOfferings = 'Feil ved henting av abonnementer',
}

const initialOfferingStateModel = {
  products: [],
  availableForChangeOfferings: [],
  services: [],
  isLoadingAdditionalServiceOfferings: false,
  loadingAdditionalServiceForOfferingId: null,
};

export interface OfferingStateModel {
  products: VoiceSubscriptionOffering[];
  availableForChangeOfferings: VoiceSubscriptionOffering[];
  services: AdditionalServicesOffering[];
  isLoadingAdditionalServiceOfferings: boolean;
  loadingAdditionalServiceForOfferingId: string;
}

@State<OfferingStateModel>({
  name: 'offerings',
  defaults: initialOfferingStateModel,
})
@Injectable()
export class OfferingState {
  private store = inject(Store);
  private offeringService = inject(OfferingService);
  private notificationService = inject(NotificationService);

  @Action(ApiGetOfferingsAction)
  public ApiGetOfferings({ dispatch }: StateContext<OfferingStateModel>) {
    return this.offeringService.getDealerOfferings().pipe(
      filter(response => !!response),
      tap(response => dispatch(new ApiGetOfferingsSuccessAction(response))),
      catchError(() => dispatch(new ApiGetOfferingsErrorAction()))
    );
  }

  @Action(ApiGetOfferingsSuccessAction)
  public addOfferings({ patchState }: StateContext<OfferingStateModel>, { offeringResponse }: ApiGetOfferingsSuccessAction) {
    patchState({ products: offeringResponse });
  }

  @Action(ApiGetOfferingsErrorAction)
  public apiGetOfferingsErrorAction() {
    this.notificationService.error(Messages.ErrorOnFetchingOfferings);
  }

  @Action(SetAvailableForChangeOfferingsAction)
  public setAvailableForChangeOfferings(
    { patchState }: StateContext<OfferingStateModel>,
    { offeringResponse }: SetAvailableForChangeOfferingsAction
  ) {
    patchState({ availableForChangeOfferings: offeringResponse });
  }

  @Action(ApiGetAdditionalServiceOfferingsAction, { cancelUncompleted: true })
  public ApiGetAdditionalServiceOfferings(
    { dispatch, getState, patchState }: StateContext<OfferingStateModel>,
    { offeringId, productId }: ApiGetAdditionalServiceOfferingsAction
  ) {
    const state = getState();
    const currentServices = state.services.find(a => a.parentOfferingId === offeringId);

    patchState({ isLoadingAdditionalServiceOfferings: true, loadingAdditionalServiceForOfferingId: offeringId });

    if (currentServices) {
      currentServices.additionalServices.serviceProducts.forEach((serviceProduct: AdditionalServiceVoice) => {
        this.automaticallyAddServiceOnSpecialCases(productId, serviceProduct);
      });

      patchState({ isLoadingAdditionalServiceOfferings: false, loadingAdditionalServiceForOfferingId: null });

      dispatch(new CombineExistingSubscriptionServicesWithOfferingServices(productId, currentServices.additionalServices));

      return;
    }

    return this.offeringService.getAdditionalServices(offeringId).pipe(
      filter((additionalServices: AdditionalServiceSet) => {
        if (!!additionalServices) {
          return true;
        }

        dispatch(new ApiGetAdditionalServiceOfferingsErrorAction(Messages.ErrorOnFetchingAdditionalServices));
        return false;
      }),
      tap((additionalServices: AdditionalServiceSet) => {
        additionalServices.serviceProducts.forEach((serviceProduct: AdditionalServiceVoice) => {
          this.automaticallyAddServiceOnSpecialCases(productId, serviceProduct);
        });

        return dispatch([
          new CombineExistingSubscriptionServicesWithOfferingServices(productId, additionalServices),
          new ApiGetAdditionalServiceOfferingsSuccessAction(
            new AdditionalServicesOffering({
              productId: productId,
              parentOfferingId: offeringId,
              additionalServices: additionalServices,
            })
          ),
        ]);
      }),
      catchError(() => dispatch(new ApiGetAdditionalServiceOfferingsErrorAction(Messages.ErrorOnFetchingAdditionalServices)))
    );
  }

  @Action(ApiGetAdditionalServiceOfferingsSuccessAction)
  public ApiGetAdditionalServiceOfferingsSuccess(
    ctx: StateContext<OfferingStateModel>,
    { additionalServices }: ApiGetAdditionalServiceOfferingsSuccessAction
  ) {
    const state = ctx.getState();
    ctx.patchState({
      services: [...state.services, additionalServices],
      isLoadingAdditionalServiceOfferings: false,
      loadingAdditionalServiceForOfferingId: null,
    });
  }

  @Action(ApiGetAdditionalServiceOfferingsErrorAction)
  public getAdditionalServiceOfferingsError(
    { patchState }: StateContext<OfferingStateModel>,
    { error }: ApiGetAdditionalServiceOfferingsErrorAction
  ) {
    patchState({ isLoadingAdditionalServiceOfferings: false, loadingAdditionalServiceForOfferingId: null });

    this.notificationService.error(error);
  }

  @Action(OfferingResetAction)
  public offeringReset(ctx: StateContext<OfferingStateModel>) {
    ctx.setState(initialOfferingStateModel);
  }

  private automaticallyAddServiceOnSpecialCases(productId: string, serviceProduct: AdditionalServiceVoice) {
    if (serviceProduct.isCampaign) {
      this.store.dispatch(new AdditionalServiceAddAction(productId, serviceProduct));
    }
  }
}
